import styles from '../styles/post.module.scss';
import { container } from 'tsyringe';
import { useState } from 'react';
import { FormData, IForumService, PlayerInfo, Post } from '../model/interfaces';
import { ReactComponent as DotsIcon } from '@ui-kit/icons/dots-three.svg';
import { RolesForPosts } from '../constants/roles';
import { ResponseCode } from '@base/utils/constants';
import { t, useBehaviorSubject } from '@base/utils/helpers';
import FormWithEditor from './formWithEditor';
import MainButton from '@ui-kit/components/mainButton';
import Flag from '@ui-kit/components/flag';
import { IMessage } from '@base/features/message/message';

function ReplyPost(props: { post: Post; poster: PlayerInfo; refreshList: () => Promise<void> }) {
  const { post, poster, refreshList } = props;
  const forumService: IForumService = container.resolve('forumService');
  const message: IMessage = container.resolve('message');
  const [isEdit, setEdit] = useState(false);
  const isModer = useBehaviorSubject(forumService.isModerating);
  const [loading, setLoading] = useState(false);

  const deletePost = () => {
    setLoading(true);
    forumService
      .deletePost(post.PostID)
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          message.success(res.message! ? res.message! : `Post deleted`, () => refreshList());
        } else {
          message.warning(res.message!);
        }
      })
      .catch((e) => {
        message.error(e.message);
      }).finally(() => setLoading(false));
  };

  const editPost = (data: FormData) => {
    setLoading(true);
    forumService
      .editPost(post.PostID, data.subject, data.message, post.Priority)
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          message.success(res.message! ? res.message! : `Post edited`, () => refreshList());
        } else {
          message.warning(res.message!);
        }
      })
      .catch((e) => {
        message.error(e.message);
      }).finally(() => setLoading(false));
  };

  const approvePost = () => {
    setLoading(true);
    forumService
      .approvePost(post.PostID)
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          message.success(res.message! ? res.message! : `Post approved`, () => refreshList());
        } else {
          message.warning(res.message!);
        }
      })
      .catch((e) => {
        message.error(e.message);
      }).finally(() => setLoading(false));
  };

  if (!poster) return null;

  return (
    <>
      <div className={styles.post}>
        <div className={styles.userInfo}>
          <h3>{post.Poster}</h3>
          <div className="avatar-overlay" style={{ height: '70px', width: '70px' }}>
            <img
              src={`https://www.gamecolony.com/cgi-bin/get_avatar.plx?uid=${post.UserID}&size=1`}
              alt="avatar"
            />
          </div>
          <div className={styles.info}>
            <Flag countryCode={poster.cCode} />
            <p className={styles[`priority-${post.Priority}`]}>{RolesForPosts[post.Priority]}</p>
            {poster.nGames && <p>{`${poster.nGames} games`}</p>}
            {poster.nWon && <p>{`${poster.nWon} won`}</p>}
          </div>
        </div>
        <div className={styles.postInfo}>
          <div className={styles.title}>
            <p>{post.Subject}</p>
            <p>{post.PostTime}</p>
          </div>
          <div className={styles.userMsg} dangerouslySetInnerHTML={{ __html: post.Message }} />

          {isModer && (
            <div className={styles.tooltip}>
              {post.State === 'N' && (
                <MainButton className={styles.approve} onClick={() => approvePost()}>
                  {t('Approve')}
                </MainButton>
              )}
              <div className={styles.editTooltip}>
                <DotsIcon height="28px" width="auto" />
                <div className={styles.tooltipBody}>
                  <button onClick={() => setEdit(!isEdit)}>{t('edit')}</button>
                  <button onClick={() => deletePost()}>{t('delete')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isEdit && (
        <FormWithEditor
          initInputValue={post.Subject}
          initMessageValue={post.Message}
          titleForm={t("edit_message")}
          placeholderInput={t("subject")}
          placeholderEditor={t("your_messsage")}
          buttonTitle={t("edit")}
          loading={loading}
          submit={editPost}
        />
      )}
    </>
  );
}

export default ReplyPost;
